import React, { FunctionComponent, useCallback, useMemo } from 'react';

import styles from './YoutubePostAnalyzed.module.scss';
import { useReport } from 'reports/pages/AddNewReport/AddNewReport';
import { Grid } from '@material-ui/core';
import { extractTitleOrSubTileOrReportNameOrDesc } from 'reports/utils/utils';
import ErrorBoundary from 'app/components/ErrorBoundary';
import CardTitle from 'app/components/CardTitle';
import MostFrequentCard from 'app/components/MostFrequentCard';
import { _t } from 'app/utils/i18n';
import {
  BigHeartIcon,
  BookmarkBigIcon,
  CalenderIcon,
  ClockIcon,
  EyeIcon,
  LanguageIcon,
  LatestUpdateIcon,
  LikeIcon,
  MessageIcon,
  PaidPromotionIcon,
  PlusIcon,
  RetweetTweetIcon,
  TweetClient,
  TweetQuotesIcon,
  TweetRepliesIcon,
  TweetType,
  UploadIcon,
} from 'app/assets/images/icons';
import AddComment from 'reports/components/AddComment';
import IconCard from 'app/components/IconCard';
import RecentMedia from 'influencers/components/RecentMedia';
import { apiRoutes } from 'posts/utils/apiRoutes';
import { path } from 'app/utils/path';
import SecondaryTable from 'app/components/Tables/SecondaryTable';
import DonutChart from 'app/components/Charts/DonutChart';
import { checkHoveringTranslated, checkTranslated } from 'app/utils/helpers';
import { nFormatter } from 'app/utils/numbersFormater';
import PostRow from 'app/components/PostRow';
import {
  storeInfluencerName,
  storeInfluencerYoutuber,
} from 'influencers/Requests';
import InfluencerInformationRow from 'app/components/InfluencerInformationRow';
import Loader from 'app/components/Loader';
import { Link, useHistory } from 'react-router-dom';
import ToolTipComp from 'app/components/ToolTipComp';
import { useQuery } from 'react-query';
import { getRelatedTags, getTrendingListVideo } from 'posts/Requests';
import { navigateWithSearchState } from 'app/utils/search-state';
import { TColumns } from 'app/components/MainTableMultiTabs/types';
import { dateTimeFormatter } from 'app/utils/dateTimeFormatter';
import ItemPopperCell from 'app/components/Tables/Cells/ItemPoperCell';
import { getHighestCountry } from 'app/utils/getLowestCountry';
import { getFromLocalStorage } from 'app/utils/localStorage';
import BaseLine from 'app/components/Charts/BaseLine';

interface Props {
  data: any;
  id: number;
  isReport?: boolean;
  isTemplate?: boolean;
  removeComments?: boolean;
  ShowConversation?: boolean;
  tabName: string;
  setActiveTab: (n: number) => void;
  items?: any[];
  isPreview?: boolean;
  isLoading?: boolean;
}

const YoutubePostAnalyzed: FunctionComponent<Props> = ({
  data,
  id,
  isReport,
  isTemplate,
  removeComments,
  tabName,
  setActiveTab,
  items,
  ShowConversation,
  isPreview,
  isLoading,
}) => {
  type TRenderCharts = { id: string; component: JSX.Element }[] | false;
  const report = useReport() as any;
  const checkIsReport = useCallback(
    (report: any, notReport: any = undefined) =>
      removeComments ? notReport : isReport && !isTemplate ? report : notReport,
    [isReport, isTemplate, removeComments],
  );
  const history = useHistory();
  const { data: relatedTags, isLoading: relatedTagsLoading } = useQuery(
    ['getYoutubeRelatedTags', Number(id), isPreview],
    getRelatedTags,
  );
  const { data: trendingListVideo } = useQuery(
    ['getYoutubeTrendingListVideo', Number(id), isPreview],
    getTrendingListVideo,
  );
  const youtubeCustomFilters = {
    video_id: Number(id),
    is_sample: isPreview,
    limit: 5,
  };
  const topWords = [
    {
      title: _t('basic:word'),
      key: 'word_name',
      render: (val: any, row: any) => (
        <div>
          {checkTranslated(row?.word_name?.original, row?.word_name?.arabic)}
        </div>
      ),
    },
    {
      title: _t('basic:usage'),
      key: 'word_count',
      render: (value: number) => <p>{nFormatter(value)}</p>,
    },
  ];
  const FrequentUsedHashtags = [
    {
      title: _t('basic:hashtags'),
      key: 'name',
      render: (val: any, row: any) => {
        return (
          <Link to={`${path.hashtags.HASHTAG_VIEW_URI}/${row?.id}`}>
            {checkTranslated(row?.name, row?.name_translated)}
          </Link>
        );
      },
    },
    {
      title: _t('trends:mentions'),
      key: 'hashtag_count',
      render: (value: number) => <p>{nFormatter(value)}</p>,
    },

    {
      title: 'basic:translation',
      key: 'title',
      render: (val: any, row: any) => (
        <>
          {row?.name_translated?.length > 0 ? (
            <ToolTipComp
              title={checkHoveringTranslated(row?.name, row?.name_translated)}
            >
              <LanguageIcon />
            </ToolTipComp>
          ) : (
            <p> -</p>
          )}
        </>
      ),
    },
  ];
  const isEn = getFromLocalStorage('LNG') === 'en';

  const channelTopVideosColumn: TColumns[] = [
    {
      title: 'trends:videos',
      key: 'videos',
      render: (val: any, row: any) => (
        <>
          <Link
            to={`${path.posts.YOUTUBE_POST_DETAILS_URI}/${row.video_id}`}
            title={
              row.title_translated?.length
                ? isEn
                  ? row.title
                  : row.title_translated
                : row.title
            }
          >
            {row.title_translated?.length > 0 ? (
              <span> {isEn ? row.title : row.title_translated}</span>
            ) : (
              row.title
            )}
          </Link>
        </>
      ),
    },
    {
      title: 'trends:views',
      key: 'views_count',
      render: (data: any) => nFormatter(data) as any,
    },
    {
      title: 'basic:translation',
      key: 'title',
      render: (val: any, row: any) => (
        <>
          {row.title_translated?.length > 0 ? (
            <ToolTipComp title={isEn ? row.title_translated : row.title}>
              <LanguageIcon />
            </ToolTipComp>
          ) : (
            <p> -</p>
          )}
        </>
      ),
    },
  ];
  const trendingListColumns: TColumns[] = [
    {
      title: _t('hashtags:trending_on'),
      key: 'created_at',
      render: (value: string) =>
        dateTimeFormatter.date(value, undefined, true) as any,
    },
    {
      title: 'country',
      key: 'countries',
      render: (data: any) => (
        <ItemPopperCell
          data={data?.slice(0, 5)?.map((item: any) => ({
            ...item,
            title: item.country_name,
            count: item.mentions,
          }))}
          headers={[_t('basic:countries'), _t('basic:rank')]}
          text={getHighestCountry(data)?.name || ''}
        />
      ),
    },
  ];
  const relatedInfluencersColumns: TColumns[] = [
    {
      title: _t('videos:name'),
      key: 'name',
      render: (val: any, row: any) => (
        <Link
          to={`${path.influencers.ANALYZED_INFLUENCER_DETAILS_URI}/${row.id}`}
        >
          {checkTranslated(row?.name?.original, row?.name?.arabic)}
        </Link>
      ),
    },
    {
      title: _t('videos:followers'),
      key: 'followers_count',
      render: (item: any) => nFormatter(item) as any,
    },
  ];
  const relatedEntitiesColumns: TColumns[] = [
    {
      title: _t('videos:name'),
      key: 'name',
      render: (val: any, row: any) => (
        <Link to={`${path.entities.ANALYZED_ENTITIES_DETAILS_URI}/${row.id}`}>
          {checkTranslated(row.name.original, row.name.arabic)}
        </Link>
      ),
    },
    {
      title: _t('videos:followers'),
      key: 'followers_count',
      render: (item: any) => nFormatter(item) as any,
    },
  ];
  const renderYoutubeCharts: TRenderCharts = [
    // {
    //   id: 'posts_youtube_video_details',
    //   component: (
    //     <Grid item sm={12} className={styles.mb}>
    //       <ErrorBoundary>
    //         <CardTitle
    //           title={_t('trends:video_details')}
    //           showBorderBottom
    //           hideIcon
    //           maxFontSize
    //           compId="posts_youtube_video_details"
    //         />
    //       </ErrorBoundary>
    //     </Grid>
    //   ),
    // },
    {
      id: 'posts_youtube_video_published_date',
      component: (
        <IconCard
          hideIfNoData
          additionalComponents={checkIsReport(
            <AddComment
              {...report}
              compId={'posts_youtube_video_published_date'}
              id={id}
            />,
          )}
          isReport={isReport}
          title={_t('videos:published_date')}
          cardData={{
            icon: <CalenderIcon />,
            count: data?.published_at,
          }}
          compId={'posts_youtube_video_published_date'}
          grid={{
            lg: checkIsReport(3, 3),
            sm: 12,
          }}
        />
      ),
    },
    {
      id: 'posts_youtube_video_views',
      component: (
        <IconCard
          withFilter={!isReport}
          hideInput
          title={_t('trends:vid_view')}
          cardData={{
            icon: <EyeIcon />,
            count: data?.views_count,
          }}
          hideDay
          compId={'posts_youtube_video_views'}
          grid={{
            lg: checkIsReport(3, 3),
            sm: 12,
          }}
          hideIfNoData={!isReport}
          additionalComponents={checkIsReport(
            <AddComment
              {...report}
              compId={'posts_youtube_video_views'}
              id={id}
            />,
          )}
          isReport={isReport}
        />
      ),
    },
    {
      id: 'posts_youtube_video_comments',
      component: (
        <IconCard
          hideIfNoData
          additionalComponents={checkIsReport(
            <AddComment
              {...report}
              compId={'posts_youtube_video_comments'}
              id={id}
            />,
          )}
          isReport={isReport}
          title={_t('trends:vid_comments')}
          cardData={{
            icon: <MessageIcon />,
            count: data?.comments_count,
          }}
          compId={'posts_youtube_video_comments'}
          grid={{
            lg: checkIsReport(3, 3),
            sm: 12,
          }}
        />
      ),
    },
    {
      id: 'posts_youtube_video_likes',
      component: (
        <IconCard
          hideIfNoData
          additionalComponents={checkIsReport(
            <AddComment
              {...report}
              compId={'posts_youtube_video_likes'}
              id={id}
            />,
          )}
          isReport={isReport}
          title={_t('trends:vid_likes')}
          cardData={{
            icon: <LikeIcon />,
            count: data?.likes_count,
          }}
          compId={'posts_youtube_video_likes'}
          grid={{
            lg: checkIsReport(3, 3),
            sm: 12,
          }}
        />
      ),
    },
    // {
    //   id: 'posts_youtube_video_paid_promotion',
    //   component: (
    //     <IconCard
    //       hideIfNoData
    //       additionalComponents={checkIsReport(
    //         <AddComment
    //           {...report}
    //           compId={'posts_youtube_video_paid_promotion'}
    //           id={id}
    //         />,
    //       )}
    //       isReport={isReport}
    //       title={_t('posts:paid_promotion')}
    //       cardData={{
    //         icon: <PaidPromotionIcon />,
    //         count: data?.grabbed_at,
    //       }}
    //       compId={'posts_youtube_video_paid_promotion'}
    //       grid={{
    //         lg: checkIsReport(3, 3),
    //         sm: 12,
    //       }}
    //     />
    //   ),
    // },
    {
      id: 'posts_youtube_video_frequent_used_hashtags',
      component: (
        <SecondaryTable
          grid={{ item: true, lg: 3, sm: 12 }}
          tableCardTitle={_t('posts:frequently_used_hashtags')}
          hideIfNoData
          additionalComponents={checkIsReport(
            <AddComment
              {...report}
              compId={'posts_youtube_video_frequent_used_hashtags'}
              id={id}
            />,
          )}
          isReport={isReport}
          compId={'posts_youtube_video_frequent_used_hashtags'}
          columns={FrequentUsedHashtags}
          moduleName={apiRoutes.youtubeFrequentUsedHashtags}
          expandAction={() => {
            window.scrollTo(0, 0);
            setActiveTab(2);
          }}
          hideFilters
          customFilters={youtubeCustomFilters}
          notPlatforms
          distributed
          hideToggleView
        />
      ),
    },
    {
      id: 'posts_youtube_video_related_tags',
      component: (
        <MostFrequentCard
          title={_t('videos:related_tags')}
          words={relatedTags}
          compId="posts_youtube_video_related_tags"
          customFilters={youtubeCustomFilters}
          hideIfNoData
          grid={{ item: true, lg: 3, sm: 12 }}
          // loading={relatedTagsLoading}
          slug={apiRoutes.youtubeRelatedTags}
          additionalComponents={
            removeComments
              ? undefined
              : checkIsReport(
                  <AddComment
                    compId={`posts_youtube_video_related_tags`}
                    id={id}
                  />,
                )
          }
          isReport={isReport}
          expandAction={() => {
            window.scrollTo(0, 0);
            setActiveTab(3);
          }}
        />
      ),
    },
    {
      id: 'posts_youtube_video_trending_list',
      component: (
        <ErrorBoundary>
          <SecondaryTable
            tableCardTitle={_t('videos:trending_list')}
            hideFilters
            hidePlatform
            columns={trendingListColumns}
            hideToggleView
            moduleName={apiRoutes.youtubeTrendingList}
            compId="posts_youtube_video_trending_list"
            customFilters={youtubeCustomFilters}
            expandUrlWithNavigate
            expandAction={() => {
              history.push(
                navigateWithSearchState(path.posts.YOUTUBE_POSTS_TABLE, {
                  activeTab: 'trending',
                  filters: {
                    video_title: {
                      label: data?.title,
                      value: data?.id,
                    },
                  },
                }),
              );
            }}
            grid={{
              lg: checkIsReport(3, 3),
              sm: 12,
            }}
            hideIfNoData
            additionalComponents={
              removeComments
                ? undefined
                : checkIsReport(
                    <AddComment
                      compId={`posts_youtube_video_trending_list`}
                      id={id}
                    />,
                  )
            }
            isReport={isReport}
          />
        </ErrorBoundary>
      ),
    },
    {
      id: 'posts_youtube_video_frequent_used_words',
      component: (
        <SecondaryTable
          grid={{
            lg: checkIsReport(3, 3),
            sm: 12,
          }}
          tableCardTitle={_t('influencers:top_words')}
          compId={'posts_youtube_video_frequent_used_words'}
          columns={topWords}
          moduleName={apiRoutes.youtubeFrequentWords}
          hideFilters
          customFilters={{ ...youtubeCustomFilters, limit: 5 }}
          notPlatforms
          distributed
          hideToggleView
          hideIfNoData={!isReport}
          additionalComponents={checkIsReport(
            <AddComment
              {...report}
              compId={'posts_youtube_video_frequent_used_words'}
              id={id}
            />,
          )}
          isReport={isReport}
        />
      ),
    },
    {
      id: 'posts_youtube_video_comments_sentiment',
      component: (
        <DonutChart
          donutTitle={
            _t('posts:comments_sentiment') +
            ` ${isReport ? `( ${_t('posts:youtube')} )` : ''}`
          }
          limit={5}
          showFilter
          hideInput
          withRefetch
          title={_t('posts:comments')}
          compId={'posts_youtube_video_comments_sentiment'}
          slug={apiRoutes.youtubeSentimentComments}
          hideIfNoData={!isReport}
          grid={{
            lg: 3,
            sm: 12,
          }}
          customFilters={youtubeCustomFilters}
          additionalComponents={checkIsReport(
            <AddComment
              {...report}
              compId={'posts_youtube_video_comments_sentiment'}
              id={id}
            />,
          )}
          isReport={isReport}
        />
      ),
    },
    // {
    //   id: 'posts_youtube_channels_details',
    //   component: (
    //     <Grid item sm={12} className={styles.mb}>
    //       <ErrorBoundary>
    //         <CardTitle
    //           title={_t('trends:channel_details')}
    //           showBorderBottom
    //           hideIcon
    //           maxFontSize
    //           compId="posts_youtube_channels_details"
    //         />
    //       </ErrorBoundary>
    //     </Grid>
    //   ),
    // },
    // {
    //   id: 'posts_youtube_channel_total_num_of_views',
    //   component: (
    //     <IconCard
    //       withFilter={!isReport}
    //       hideInput
    //       title={_t('videos:total_num_view')}
    //       cardData={{
    //         icon: <EyeIcon />,
    //         count: data?.channel?.views_count,
    //       }}
    //       hideDay
    //       compId={'posts_youtube_channel_total_num_of_views'}
    //       grid={{
    //         lg: checkIsReport(3, 3),
    //         sm: 12,
    //       }}
    //       hideIfNoData={!isReport}
    //       additionalComponents={checkIsReport(
    //         <AddComment
    //           {...report}
    //           compId={'posts_youtube_channel_total_num_of_views'}
    //           id={id}
    //         />,
    //       )}
    //       isReport={isReport}
    //     />
    //   ),
    // },
    // {
    //   id: 'posts_youtube_channel_uploaded_count',
    //   component: (
    //     <IconCard
    //       withFilter={!isReport}
    //       hideInput
    //       title={_t('trends:uploaded_video')}
    //       cardData={{
    //         icon: <UploadIcon />,
    //         count: data?.channel?.uploaded_count,
    //       }}
    //       hideDay
    //       compId={'posts_youtube_channel_uploaded_count'}
    //       grid={{
    //         lg: checkIsReport(3, 3),
    //         sm: 12,
    //       }}
    //       hideIfNoData={!isReport}
    //       additionalComponents={checkIsReport(
    //         <AddComment
    //           {...report}
    //           compId={'posts_youtube_channel_uploaded_count'}
    //           id={id}
    //         />,
    //       )}
    //       isReport={isReport}
    //     />
    //   ),
    // },
    // {
    //   id: 'posts_youtube_channel_subscriber_count',
    //   component: (
    //     <IconCard
    //       withFilter={!isReport}
    //       hideInput
    //       title={_t('trends:channel_sub')}
    //       cardData={{
    //         icon: <PlusIcon />,
    //         count: data?.channel?.subscriber_count,
    //       }}
    //       hideDay
    //       compId={'posts_youtube_channel_subscriber_count'}
    //       grid={{
    //         lg: checkIsReport(3, 3),
    //         sm: 12,
    //       }}
    //       hideIfNoData={!isReport}
    //       additionalComponents={checkIsReport(
    //         <AddComment
    //           {...report}
    //           compId={'posts_youtube_channel_subscriber_count'}
    //           id={id}
    //         />,
    //       )}
    //       isReport={isReport}
    //     />
    //   ),
    // },
    // {
    //   id: 'posts_youtube_channel_top_videos_table',
    //   component: (
    //     <Grid item sm={12} md={6}>
    //       <ErrorBoundary>
    //         <SecondaryTable
    //           tableCardTitle={_t('trends:channel_top_videos')}
    //           hideFilters
    //           hidePlatform
    //           columns={channelTopVideosColumn}
    //           hideToggleView
    //           data={data?.top_videos}
    //           compId="posts_youtube_channel_top_videos_table"
    //           hideIfNoData
    //           additionalComponents={
    //             removeComments
    //               ? undefined
    //               : checkIsReport(
    //                   <AddComment
    //                     compId={`posts_youtube_channel_top_videos_table`}
    //                     id={id}
    //                   />,
    //                 )
    //           }
    //           isReport={isReport}
    //         />
    //       </ErrorBoundary>
    //     </Grid>
    //   ),
    // },
    // {
    //   id: 'posts_youtube_channel_gained_subscribers',
    //   component: (
    //     <Grid item md={6} sm={12}>
    //       <ErrorBoundary>
    //         <BaseLine
    //           baseLineTitle={_t('trends:gained_subs')}
    //           slugName={apiRoutes.channelSubscribers}
    //           showCustomTooltip
    //           hideNames
    //           noDayInTimeInterval
    //           timeIntervalFilter
    //           customFilters={{
    //             video_ids: [Number(id)],
    //             is_sample: isPreview,
    //           }}
    //           compId={`posts_youtube_channel_gained_subscribers`}
    //           additionalComponents={
    //             removeComments
    //               ? undefined
    //               : checkIsReport(
    //                   <AddComment
    //                     compId={`posts_youtube_channel_gained_subscribers`}
    //                     id={id}
    //                   />,
    //                 )
    //           }
    //           isReport={isReport}
    //         />
    //       </ErrorBoundary>
    //     </Grid>
    //   ),
    // },
    // {
    //   id: 'posts_youtube_channel_views',
    //   component: (
    //     <Grid item md={6} sm={12}>
    //       <ErrorBoundary>
    //         <BaseLine
    //           baseLineTitle={_t('trends:channel_views')}
    //           slugName={apiRoutes.channelViews}
    //           showCustomTooltip
    //           hideNames
    //           noDayInTimeInterval
    //           timeIntervalFilter
    //           customFilters={{
    //             video_ids: [Number(id)],
    //             is_sample: isPreview,
    //           }}
    //           compId={`posts_youtube_channel_views`}
    //           additionalComponents={
    //             removeComments
    //               ? undefined
    //               : checkIsReport(
    //                   <AddComment
    //                     compId={`posts_youtube_channel_views`}
    //                     id={id}
    //                   />,
    //                 )
    //           }
    //           isReport={isReport}
    //         />
    //       </ErrorBoundary>
    //     </Grid>
    //   ),
    // },
    // {
    //   id: 'posts_youtube_channel_activity',
    //   component: (
    //     <Grid item md={6} sm={12}>
    //       <ErrorBoundary>
    //         <BaseLine
    //           baseLineTitle={_t('videos:channel_activity')}
    //           slugName={apiRoutes.channelActivities}
    //           showCustomTooltip
    //           hideNames
    //           noDayInTimeInterval
    //           timeIntervalFilter
    //           customFilters={{
    //             video_ids: [Number(id)],
    //             is_sample: isPreview,
    //           }}
    //           compId={`posts_youtube_channel_activity`}
    //           additionalComponents={
    //             removeComments
    //               ? undefined
    //               : checkIsReport(
    //                   <AddComment
    //                     compId={`posts_youtube_channel_activity`}
    //                     id={id}
    //                   />,
    //                 )
    //           }
    //           isReport={isReport}
    //         />
    //       </ErrorBoundary>
    //     </Grid>
    //   ),
    // },
    // {
    //   id: 'videos_video_view_related_influencers',
    //   component: !!data?.related_influencers?.length ? (
    //     <Grid item lg={checkIsReport(6)} sm={6} md={3}>
    //       <ErrorBoundary>
    //         <SecondaryTable
    //           tableCardTitle={_t('videos:related_influencers')}
    //           hideFilters
    //           hidePlatform
    //           columns={relatedInfluencersColumns}
    //           data={data?.related_influencers}
    //           hideToggleView
    //           compId="videos_video_view_related_influencers"
    //           additionalComponents={
    //             removeComments
    //               ? undefined
    //               : checkIsReport(
    //                   <AddComment
    //                     compId={`videos_video_view_related_entities`}
    //                     id={id}
    //                   />,
    //                 )
    //           }
    //         />
    //       </ErrorBoundary>
    //     </Grid>
    //   ) : (
    //     <></>
    //   ),
    // },
    // {
    //   id: 'videos_video_view_related_entities',
    //   component: !!data?.related_entities?.length ? (
    //     <Grid item lg={checkIsReport(6)} sm={6} md={3}>
    //       <ErrorBoundary>
    //         <SecondaryTable
    //           tableCardTitle={_t('videos:related_entities')}
    //           hideFilters
    //           hidePlatform
    //           columns={relatedEntitiesColumns}
    //           data={data?.related_entities}
    //           hideToggleView
    //           compId="videos_video_view_related_entities"
    //           additionalComponents={
    //             removeComments
    //               ? undefined
    //               : checkIsReport(
    //                   <AddComment
    //                     compId={`videos_video_view_related_entities`}
    //                     id={id}
    //                   />,
    //                 )
    //           }
    //         />
    //       </ErrorBoundary>
    //     </Grid>
    //   ) : (
    //     <></>
    //   ),
    // },
    {
      id: 'posts_youtube_video_influencer',
      component: (
        <InfluencerInformationRow
          compId="posts_youtube_video_influencer"
          data={data}
          title={_t('posts:influencer_details')}
          storeInfluencer={storeInfluencerYoutuber}
          openInSource={_t('hashtags:open_in_youtube')}
          grid={{
            sm: 12,
          }}
          isChannel={true}
        />
      ),
    },

    {
      id: 'posts_youtube_video_details',
      component: isLoading ? (
        <Loader />
      ) : (
        <PostRow
          compId="posts_youtube_video_details"
          data={data}
          title={_t('posts:youtube_posts')}
          tabName="youtube"
          isReport={isReport || isTemplate}
          grid={{
            sm: 12,
          }}
          showPosts
        />
      ),
    },
  ];
  const renderChartsList = (chartList: any) =>
    chartList.map((item: any, index: number) =>
      item?.component
        ? React.cloneElement(item.component, { key: item?.id || index })
        : null,
    );
  const renderPage = () => {
    if (tabName == 'youtube') return renderChartsList(renderYoutubeCharts);
    if (items) {
      const paragraph = items?.map((item) =>
        extractTitleOrSubTileOrReportNameOrDesc(item),
      );
      const chartsFilter = renderYoutubeCharts?.map((item: any) =>
        items?.some((i: any) => i?.slug === item?.id)
          ? {
              ...item,
              order: items?.find((comp) => comp.slug === item.id)?.order,
            }
          : null,
      );

      return renderChartsList(
        [...chartsFilter, ...paragraph]
          .filter(Boolean)
          .sort((a, b) => a.order - b.order),
      );
    }
  };
  const memoizedRenderPage = useMemo(
    () => renderPage(),

    [tabName, report, data, id],
  );
  return (
    <Grid container spacing={4}>
      {memoizedRenderPage}
    </Grid>
  );
};

export default YoutubePostAnalyzed;
